import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LoginButton from "./LoginButton";
import ReactGA from "react-ga4";

import { SUPPORTED_LANGUAGES } from "../i18n";
import {BookRideButton} from "./BookingButton";

// Navigation Configuration
const NAV_ITEMS = [
  { label: "About", route: "/" },
  { label: "Book a Ride", route: "/book" },
  { label: "Passenger", route: "/passenger" },
  { label: "Driver", route: "/driver" },
  { label: "Cross-border Travel", route: "/cross-border" },
  { label: "Shared Ride", route: "/shared-ride" },
  { label: "App", route: null }
];

const MOBILE_NAV_ITEMS = [
  { label: "About", route: "/" },
  { label: "Book a Ride", route: "/book" },
  { label: "Passenger", route: "/passenger" },
  { label: "Driver", route: "/driver" },
  { label: "Cross-border Travel", route: "/cross-border" },
  { label: "Shared Ride", route: "/shared-ride" },
];

export default function Navbar({ footerRef, isBooking = false, isLogin = false }: {
  footerRef: React.RefObject<HTMLDivElement>,
  isBooking?: boolean,
  isLogin?: boolean
}) {
  const [isScrolled, setIsScrolled] = useState(false);
  const navbarHeight = isScrolled ? "64px" : "80px"; // Approximate heights based on padding

  // Handle scroll event to change navbar appearance
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);

    // Update CSS variable when navbar height changes
    document.documentElement.style.setProperty('--navbar-height', navbarHeight);

    return () => window.removeEventListener('scroll', handleScroll);
  }, [isScrolled, navbarHeight]);

  const scrollToFooter = () => {
    if (footerRef.current) {
      const footerElement = footerRef.current;
      const navbarHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--navbar-height') || '80', 10);

      // Calculate target position with navbar offset
      const targetPosition = footerElement.getBoundingClientRect().top + window.pageYOffset - navbarHeight;
      const startPosition = window.pageYOffset;
      const distance = targetPosition - startPosition;
      const duration = 1000;
      const startTime = performance.now();

      // Easing function (ease-in-out cubic)
      const easeInOutCubic = (t: number) => {
        return t < 0.5
          ? 4 * t * t * t
          : 1 - Math.pow(-2 * t + 2, 3) / 2;
      };

      // Scroll animation function
      const scrollAnimation = (currentTime: number) => {
        const elapsedTime = currentTime - startTime;
        const progress = Math.min(elapsedTime / duration, 1);
        const easedProgress = easeInOutCubic(progress);

        const currentScrollPosition = startPosition + distance * easedProgress;
        window.scrollTo(0, currentScrollPosition);

        if (progress < 1) {
          requestAnimationFrame(scrollAnimation);
        }
      };

      requestAnimationFrame(scrollAnimation);
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300
                      ${isScrolled
      ? 'bg-[#0A1854] shadow-md py-2'
      : 'bg-transparent bg-opacity-90 py-4'}`}>
      <div className="container mx-auto flex items-center justify-between px-4 sm:px-6 lg:px-8">
        <LogoSection isScrolled={isScrolled} />
        <div className="flex items-center">
          <DesktopNav scrollToFooter={scrollToFooter} />
          <div className="flex items-center">
            <MobileNav scrollToFooter={scrollToFooter}/>
            <div className="h-6 w-px bg-gray-300 mx-2 md:flex"></div>
            <LanguageChanger />
          </div>
        </div>
      </div>
      { isLogin ? (<div/>) : (
        <div className="absolute right-4 sm:right-8 md:right-12 lg:right-16 z-40 flex flex-col items-end" style={{ top: 'calc(100% + 30px)' }}>
          { isBooking ? (
            <LoginButton />
          ) : (
            <>
              <LoginButton />
              <>
                <div className="hidden sm:block">
                  <BookRideButton />
                </div>
                <div className="sm:hidden">
                  <BookRideButton isMobile={true} />
                </div>
              </>
            </>
          ) }
        </div>
      )}
      {/* WhatsApp Button */}
      <a
        href="https://wa.link/xzle92"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-20 right-6 bg-[#25D366] text-white rounded-full p-3 shadow-lg hover:bg-[#128C7E] transition-colors duration-300 flex items-center justify-center z-50 cursor-pointer group"
        aria-label="Contact us on WhatsApp"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="28"
          height="28"
          fill="currentColor"
          viewBox="0 0 16 16"
          className="group-hover:scale-110 transition-transform duration-300"
        >
          <path d="M13.601 2.326A7.854 7.854 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.933 7.933 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.898 7.898 0 0 0 13.6 2.326zM7.994 14.521a6.573 6.573 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.557 6.557 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592zm3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.729.729 0 0 0-.529.247c-.182.198-.691.677-.691 1.654 0 .977.71 1.916.81 2.049.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232z"/>
        </svg>
      </a>
    </header>
  );
}

const LogoSection = ({ isScrolled }: { isScrolled: boolean }) => {
  return (
    <div className="flex items-center">
      <Link to="/">
        <img
          src="/image/logo/GBA_logo.png"
          alt="GBA Taxi Logo"
          className={`transition-all duration-300 ${isScrolled ? 'h-16 w-auto' : 'h-20 w-auto sm:h-24'}`}
        />
      </Link>
      <div className="flex flex-col ml-2 gap-3">
        <p className="font-medium leading-tight">GBA Taxi</p>
        <p className="text-sm font-medium leading-tight">湾驹出行</p>
      </div>
    </div>
  );
}

const LanguageChanger = () => {
  const { i18n } = useTranslation();

  const changeAppLanguage = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    localStorage.setItem("language", languageCode);
  };

  return (
    <section className="flex items-center mr-2 lg:mr-0">
      {SUPPORTED_LANGUAGES.map(({ code, label }) => (
        <button
          key={code}
          id={`${code}-btn`}
          className={`px-2 py-1 relative group transition-colors duration-300 text-sm sm:text-base md:text-lg ${
            i18n.language === code ? 'font-bold' : 'font-medium'
          }`}
          onClick={() => changeAppLanguage(code)}
        >
          <span className="relative inline-block">
            {label}
            <span
              className={`absolute left-0 bottom-[-4px] h-[2px] bg-current
                transition-all duration-300 ease-in-out
                origin-left ${i18n.language === code
                ? 'w-full scale-x-100'
                : 'w-0 group-hover:w-full scale-x-0 group-hover:scale-x-100'}`}
            />
          </span>
        </button>
      ))}
    </section>
  );
};

function NavItemWithHoverEffect({ id, label, route, onClick }: {
  id: string,
  label: string,
  route?: string | null,
  onClick?: () => void
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const content = (
    <span className="relative inline-block" id={id}>
      {t(label)}
      <span
        className="absolute left-0 bottom-[-4px] h-[2px] bg-current
          w-0 group-hover:w-full
          transition-all duration-300 ease-in-out
          origin-left scale-x-0 group-hover:scale-x-100"
      />
    </span>
  );

  return route ? (
    <button
      onClick={() => {
        // console.log(`Clicked ${label} Button`);
        ReactGA.event({
          category: "User Interaction",
          action: `Clicked ${label} Button`,
          label: label,
        })
        navigate(route);
      }}
      id={`${label}-btn`}
      className="px-3 py-2 relative group transition-colors duration-300 text-sm md:text-base"
    >
      {content}
    </button>
  ) : (
    <button
      className="px-3 py-2 relative group transition-colors duration-300 text-sm md:text-base"
      id='App-btn'
      onClick={onClick}
    >
      {content}
    </button>
  );
}

function DesktopNav({ scrollToFooter }: { scrollToFooter: () => void }) {
  return (
    <nav className="hidden md:flex items-center">
      {NAV_ITEMS.map(({ label, route }) => (
        <NavItemWithHoverEffect
          key={`${label}-btn`}
          id={`${label}-btn`}
          label={label}
          route={route}
          onClick={label === "App" ? scrollToFooter : undefined}
        />
      ))}
    </nav>
  );
}

function MobileNav({ scrollToFooter }: { scrollToFooter: () => void }) {
  const {t} = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  return (
    <div className="md:hidden relative">
      <button
        className="p-2 rounded-md"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Toggle menu"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          {isOpen ? (
            // X icon when menu is open
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M6 18L18 6M6 6l12 12"
            />
          ) : (
            // Hamburger icon when menu is closed
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M4 6h16M4 12h16M4 18h7"
            />
          )}
        </svg>
      </button>

      {/* Dropdown menu */}
      <div
        className={`absolute right-0 top-full mt-1 w-48 bg-white rounded-md shadow-lg overflow-hidden transition-all duration-200 origin-top-right z-50 text-red-500 
          ${isOpen ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-0'}`}
      >
        <ul className="py-1">
          {MOBILE_NAV_ITEMS.map(({label, route}) => (
            <li key={route}>
              <button
                id={`${label}-mobile-btn`}
                className="block px-4 py-2 text-sm hover:bg-gray-100 transition-colors"
                onClick={() => {
                  ReactGA.event({
                    category: "User Interaction",
                    action: `Clicked ${label} Button (Mobile)`,
                    label: `${label} (Mobile)`
                  })
                  setIsOpen(false);
                  navigate(route);
                }}
              >
                {t(label)}
              </button>
            </li>
          ))}
          <li>
            <button
              className="block px-4 py-2 text-sm hover:bg-gray-100 transition-colors"
              onClick={() => {
                scrollToFooter();
                navigate('#footer');
                setIsOpen(false);
              }}
            >
              {t('App')}
            </button>
          </li>
        </ul>
      </div>
    </div>
  );
}